import Vue from "vue";
import { AbacoWebModule, RouterReadyEvent, MenuItem, ApplicationReadyEvent } from '@abaco/web-common/src/app';
import globals from "./globals";
import myRoutes from "./routes";
import CropPlanVuexModule from './state';
import { HTTPClient } from '@abaco/web-common/src/HTTP';
import { createMenu } from './menu';
import BlockApi from "./api/BlockApi";
import CouncilApi from './api/CouncilApi';
import PlantingApi from './api/PlantingApi';
import PlotApi from "./api/PlotApi";
import VegCycleApi from "./api/VegCycleApi";
import PlotGroupApi from './api/PlotGroupApi';
import CropFilterApi from './api/CropFilterApi';
import PhenologyApi from './api/PhenologyApi';
import ThemeApi from "./api/ThemeApi";
import FarmerRegistryModule from '@abaco/farm-registry/src/modules/farm-registry';
import MapModule from '@abaco/map-web-module/src/modules/map-web-module';
import GlobalStateModule from '@abaco/portal-global-state/src/modules/global-state';
import DashboardChartsModule from "@abaco/dashboard-module/src/modules/charts/index";
import DashboardModule from "@abaco/dashboard-module/src/modules/dashboard/index";
import SatellitesWidgetModule from "@abaco/widgets-container-module/src/modules/satellites-widgets/index";
import UserModule from "@abaco/user-module/src/modules/user-module/index";
import PrintCropPlanModule from "../print-crop-plan-module/index";
import AssetsModule from "@abaco/assets-module/src/modules/assets-module";
import HelperModule from "@abaco/helper-module/src/modules/helper-module/index";
import ChartApi from './api/ChartApi';
import { Block } from "./models/Block";
import ContractApi from "./api/ContractApi";
import SSOModule from "@abaco/sso-web-module/src/abaco-module";
import SsoVuexModule from "@abaco/sso-web-module/src/abaco-module/state";
import DocumentApi from "./api/DocumentApi";
import SatelliteIndexApi from "./api/SatelliteIndexApi";
import NumberUtils from "@abaco/web-components/src/components/input/NumberUtils"
import { HA_UNIT } from "./api/utils";
import AIApi from "./api/AIApi";
import VarietiesApi from "./api/VarietiesApi";
import ColdirettiApi from "./api/ColdirettiApi";

export const MODULE_ID = "crop_plan_module";

const state = new CropPlanVuexModule();

interface ConstructorOptions {
    environment?: string;
}

export default class CropPlanModule implements AbacoWebModule {
    public static MODULE_ID = MODULE_ID;

    public readonly environment!: string;

    private apiCreated = false;
    private blkApi!: BlockApi;
    private councApi!: CouncilApi;
    private plantApi!: PlantingApi;
    private pltApi!: PlotApi;
    private vegCycApi!: VegCycleApi;
    private pltGrpApi!: PlotGroupApi;
    private chrtApi!: ChartApi;
    private vrtiesApi!: VarietiesApi;
    private crfApi!: CropFilterApi;
    private phApi!: PhenologyApi;
    private cntrApi!: ContractApi;
    private docApi!: DocumentApi;
    private aiApi!: AIApi;
    private thApi!: ThemeApi;
    private satApi!: SatelliteIndexApi;
    private coldApi!: ColdirettiApi;

    private ssoStore!: SsoVuexModule;

    readonly id = MODULE_ID;
    readonly routes = myRoutes;
    readonly globalComponents = globals;
    readonly vuexModule = state;

    readonly context = "FARM_CROP_PLAN_MAP";

    additionalI18N = ['web-components']
    readonly cropPlanMapBlackListedEntities = ['LOT'];
    menu: MenuItem[] = [];

    constructor(options?: ConstructorOptions) {
        this.environment = options?.environment || "portal"
    }

    dependsOn = {
        [FarmerRegistryModule.MODULE_ID]: () => new FarmerRegistryModule(),
        [MapModule.MODULE_ID]: () => new MapModule(),
        [GlobalStateModule.MODULE_ID]: () => new GlobalStateModule(),
        [DashboardChartsModule.MODULE_ID]: () => new DashboardChartsModule(),
        [DashboardModule.MODULE_ID]: () => new DashboardModule(),
        [UserModule.MODULE_ID]: () => new UserModule(),
        [PrintCropPlanModule.MODULE_ID]: () => new PrintCropPlanModule(),
        [HelperModule.MODULE_ID]: () => new HelperModule(),
        [AssetsModule.MODULE_ID]: () => new AssetsModule(),
        [SatellitesWidgetModule.MODULE_ID]: () => new SatellitesWidgetModule()
    }

    onRouterReady(evt: RouterReadyEvent) {
        this.menu = createMenu(evt.router);
        Vue.filter("TO_AREA_HA", (val: number, fromFormat?: string) => {
            if (fromFormat === HA_UNIT) {
                return NumberUtils.stringify(val, 4)
            }
            return NumberUtils.stringify(val / 10000, 4)
        });
    }

    onApplicationReady(evt: ApplicationReadyEvent) {
        const ssoModule = evt.modules.find(m => m.id === SSOModule.MODULE_ID);
        if (!ssoModule) {
            throw new Error("ssoModule not found");
        }
        this.ssoStore = ssoModule.vuexModule as SsoVuexModule;
    }

    async beforeEnterAnyRoutes(http: HTTPClient) {
        if (this.apiCreated) {
            return;
        }
        this.apiCreated = true;

        const BlockApi = (await import("./api/BlockApi")).default;
        this.blkApi = new BlockApi(http);

        const CouncilApi = (await import("./api/CouncilApi")).default;
        this.councApi = new CouncilApi(http);

        const PlantingApi = (await import("./api/PlantingApi")).default;
        this.plantApi = new PlantingApi(http);

        const PlotApi = (await import("./api/PlotApi")).default;
        this.pltApi = new PlotApi(http);

        const VegCycleApi = (await import("./api/VegCycleApi")).default;
        this.vegCycApi = new VegCycleApi(http);

        const PlotGroupApi = (await import("./api/PlotGroupApi")).default;
        this.pltGrpApi = new PlotGroupApi(http);

        const ChartApi = (await import("./api/ChartApi")).default;
        this.chrtApi = new ChartApi(http);

        const VarietiesApi = (await import("./api/VarietiesApi")).default;
        this.vrtiesApi = new VarietiesApi(http);

        const CropFilterApi = (await import("./api/CropFilterApi")).default;
        this.crfApi = new CropFilterApi(http);

        const PhenologyApi = (await import("./api/PhenologyApi")).default;
        this.phApi = new PhenologyApi(http);

        const ContractApi = (await import("./api/ContractApi")).default;
        this.cntrApi = new ContractApi(http, this.ssoStore);

        const DocumentApi = (await import("./api/DocumentApi")).default;
        this.docApi = new DocumentApi(http);

        const AIApi = (await import("./api/AIApi")).default;
        this.aiApi = new AIApi(http);

        const ThemeApi = (await import("./api/ThemeApi")).default;
        this.thApi = new ThemeApi(http)

        const SatelliteIndexApi = (await import("./api/SatelliteIndexApi")).default;
        this.satApi = new SatelliteIndexApi(http)

        const ColdirettiApi = (await import("./api/ColdirettiApi")).default;
        this.coldApi = new ColdirettiApi(http)
    }

    public get blockApi(): BlockApi {
        return this.blkApi;
    }

    public get councilApi(): CouncilApi {
        return this.councApi;
    }

    public get plantingApi(): PlantingApi {
        return this.plantApi;
    }

    public get plotApi(): PlotApi {
        return this.pltApi;
    }

    public get vegCycleApi(): VegCycleApi {
        return this.vegCycApi;
    }

    public get plotGroupApi(): PlotGroupApi {
        return this.pltGrpApi;
    }

    public get chartApi(): ChartApi {
        return this.chrtApi;
    }

    public get varietiesApi(): VarietiesApi {
        return this.vrtiesApi;
    }

    public get cropFilterApi(): CropFilterApi {
        return this.crfApi;
    }

    public get phenologyApi(): PhenologyApi {
        return this.phApi;
    }

    public get contractApi(): ContractApi {
        return this.cntrApi;
    }

    public get themeApi(): ThemeApi {
        return this.thApi
    }

    public get documentApi(): DocumentApi {
        return this.docApi;
    }

    public get artificialIntellgenceApi(): AIApi {
        return this.aiApi;
    }

    public get satelliteIndexApi(): SatelliteIndexApi {
        return this.satApi;
    }

    public get coldirettiApi(): ColdirettiApi {
        return this.coldApi;
    }

    public setBlock(val: Block) {
        this.vuexModule.curBlock = val;
    }

}
